/**
 * Preset Schema Definitions
 * Clean TypeScript interfaces for chord progressions and guitar arrangements
 */

// ================================================================================
// CORE TYPES
// ================================================================================

/**
 * Difficulty levels for progressions
 */
export type DifficultyLevel = 'beginner' | 'intermediate' | 'advanced';

/**
 * Source types for progressions
 */
export type ProgressionSource = 'user' | 'standard';

/**
 * Standard genre categories
 */
export type GenreCategory = 'pop' | 'rock' | 'jazz' | 'folk' | 'blues' | 'practice';

/**
 * Access levels for progressions in freemium model
 */
export type AccessLevel = 'free' | 'premium_preview' | 'premium_full';

/**
 * Premium features that can be used in progressions
 */
export type PremiumFeature = 
  | 'multiple_caged_shapes'
  | 'overlay_visualizations' 
  | 'scale_degrees'
  | 'modal_scales'
  | 'advanced_visualizations'
  | 'unlimited_guitars';

/**
 * Visualization settings for guitar displays
 */
export interface CagedVisualizationSettings {
  LINK: boolean;
}

/**
 * Overlay visualization settings
 */
export interface OverlayVisualizationSettings {
  GLOW: boolean;
  GROW: boolean;
  PULSE: boolean;
}

/**
 * Default CAGED visualization settings
 * Standard defaults for CAGED shape visualizations
 */
export const DEFAULT_CAGED_VISUALIZATIONS: CagedVisualizationSettings = {
  LINK: true
};

/**
 * Default CAGED shapes (all inactive)
 * Standard defaults for CAGED shape selection
 */
export const DEFAULT_CAGED_SHAPES: CagedShapes = {
  C: false,
  A: false,
  G: false,
  E: false,
  D: false
};

/**
 * Default overlay visualization settings
 * Auto-enables GLOW when creating new overlays
 */
export const DEFAULT_OVERLAY_VISUALIZATIONS: OverlayVisualizationSettings = {
  GLOW: true,  // Auto-enable GLOW for new overlays
  GROW: false,
  PULSE: false
};

/**
 * Default root note and scale configuration
 */
export const DEFAULT_ROOT_CONFIG = {
  note: 'C' as Note,
  scale: 'MAJOR' as Scale,
  showScaleDegrees: false
};

/**
 * Create a complete default guitar arrangement
 * Used by createGuitar when no specific configuration is provided
 */
export const createDefaultGuitarArrangement = (): Omit<GuitarArrangement, 'id'> => ({
  root: DEFAULT_ROOT_CONFIG,
  caged: {
    activeShapes: { ...DEFAULT_CAGED_SHAPES },
    quality: 'MAJOR',
    visualizations: { ...DEFAULT_CAGED_VISUALIZATIONS }
  }
});

/**
 * CAGED shape configuration
 * All properties are optional since shapes can be selectively activated
 */
export interface CagedShapes {
  E?: boolean;
  A?: boolean;
  G?: boolean;
  C?: boolean;
  D?: boolean;
}

export type Note = "C" | "C#" | "D" | "D#" | "E" | "F" | "F#" | "G" | "G#" | "A" | "A#" | "B";

// All possible scales used in guitar arrangements
export type Scale = 
  // Diatonic Scales
  | 'MAJOR' 
  | 'MINOR' 
  | 'HARMONIC_MINOR' 
  | 'MELODIC_MINOR'
  // Modal Scales
  | 'DORIAN' 
  | 'PHRYGIAN' 
  | 'LYDIAN' 
  | 'MIXOLYDIAN' 
  | 'LOCRIAN'
  // Pentatonic Scales
  | 'PENTATONIC_MAJOR' 
  | 'PENTATONIC_MINOR'
  // Blues and Other Scales
  | 'BLUES' 
  | 'CHROMATIC' 
  | 'WHOLE_TONE' 
  | 'OCTATONIC_HALF_WHOLE' 
  | 'OCTATONIC_WHOLE_HALF'
  // Triads
  | 'MAJOR_TRIAD' 
  | 'MINOR_TRIAD' 
  | 'DIMINISHED_TRIAD' 
  | 'AUGMENTED_TRIAD'
  // 7th Chords
  | 'DOMINANT_7TH' 
  | 'MAJOR_7TH' 
  | 'MINOR_7TH' 
  | 'MINOR_MAJOR_7TH' 
  | 'DIMINISHED_7TH' 
  | 'HALF_DIMINISHED_7TH' 
  | 'AUGMENTED_7TH' 
  | 'AUGMENTED_MAJOR_7TH';

// For overlay shapes - matches the exact values used in the overlay dropdown UI
export type OverlayQuality = 
  | 'MAJOR_TRIAD' 
  | 'MINOR_TRIAD' 
  | 'DOMINANT_7TH' 
  | 'MAJOR_7TH' 
  | 'MINOR_7TH' 
  | 'HALF_DIMINISHED_7TH';

// For CAGED shapes - simplified quality values used by the CAGED system
export type CagedQuality = 'MAJOR' | 'MINOR';

// ================================================================================
// GUITAR ARRANGEMENT TYPES
// ================================================================================

/**
 * Individual guitar arrangement within a progression
 */
export interface GuitarArrangement {
  id: string;

  // Root note and scale quality
  root: {
    note: Note;
    scale: Scale;
    showScaleDegrees: boolean;
  };

  // Overlay note 
  overlay?: {
    note: Note;
    quality: OverlayQuality;
    visualizations: OverlayVisualizationSettings;
  };

  // CAGED shapes
  caged?: {
    activeShapes: CagedShapes;
    quality: CagedQuality;
    visualizations: CagedVisualizationSettings;
  };
}

// ================================================================================
// PROGRESSION METADATA
// ================================================================================

/**
 * Rich metadata for progressions
 */
export interface ProgressionMetadata {
  // Music theory information
  pattern?: string;           // Roman numeral pattern (e.g., 'I vi IV V')
  key?: string;              // Key signature (e.g., 'C Major')
  description?: string;       // Human-readable description
  examples?: string[];        // Example songs using this progression
  difficulty?: DifficultyLevel;
  genre?: GenreCategory;
  
  // Freemium access control
  accessLevel?: AccessLevel;  // Access level for this progression
  premiumFeatures?: PremiumFeature[]; // What premium features this progression uses
  freeAlternativeId?: string; // Links to simplified free version
  
  // Timestamps
  created: number;           // Creation timestamp
  modified: number;          // Last modification timestamp
  
  // Usage tracking
  usageCount?: number;       // How many times loaded/used
  lastUsed?: number;         // Last usage timestamp
  
  // User organization
  tags?: string[];           // User-defined tags
  favorite?: boolean;        // User favorite flag
  
  // Technical metadata
  guitarCount: number;       // Number of guitar arrangements
  version: string;           // Schema version for future migrations
}

// ================================================================================
// CORE PROGRESSION TYPES
// ================================================================================

/**
 * Core progression data structure
 * Schema-first: arrangements are the source of truth, chords derived on-demand
 */
export interface Progression {
  id: string;                    // Unique identifier
  name: string;                  // Display name
  source: ProgressionSource;     // Whether user-created or from standard library
  arrangements: GuitarArrangement[]; // Required guitar arrangements - source of truth
  metadata: ProgressionMetadata; // Rich metadata
}

// ================================================================================
// API OPERATION RESULTS
// ================================================================================

/**
 * Standard API response wrapper
 */
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  timestamp: number;
}

/**
 * Save operation result
 */
export interface SaveProgressionResponse extends ApiResponse<Progression> {
  created?: boolean;            // Whether this was a new progression or update
}

/**
 * Load operation result
 */
export interface LoadProgressionResponse extends ApiResponse<{
  progression: Progression;
  arrangements: GuitarArrangement[];
}> {
  // Additional load-specific data could go here
}

/**
 * Delete operation result
 */
export interface DeleteProgressionResponse extends ApiResponse<void> {
  deletedId?: string;
}


// ================================================================================
// STORAGE CONFIGURATION
// ================================================================================

/**
 * Storage configuration and constants
 */
export const STORAGE_CONFIG = {
  // localStorage keys
  PROGRESSIONS_LIST_KEY: 'fret_progressions_list',
  PROGRESSION_PREFIX: 'fret_progression_',
  METADATA_KEY: 'fret_progressions_metadata',
  
  // Schema version for migrations
  CURRENT_VERSION: '2.0.0',
  
  // Limits
  MAX_NAME_LENGTH: 50,
  MAX_DESCRIPTION_LENGTH: 200,
  MAX_PROGRESSIONS: 1000,
  MAX_CHORDS_PER_PROGRESSION: 16,
} as const;

// ================================================================================
// VALIDATION SCHEMAS
// ================================================================================

/**
 * Validation result
 */
export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings?: string[];
}

/**
 * Validation utilities type definitions
 */
export interface ValidationRules {
  validateProgression: (progression: Progression) => ValidationResult;
  validateGuitarArrangement: (arrangement: GuitarArrangement) => ValidationResult;
  validateChord: (chord: string) => boolean;
  sanitizeName: (name: string) => string;
}